import { useCallback, useMemo, useState } from "react"

import LumenLogo from "@/components/icons/LumenLogo"
import UserGradientCircle from "@/components/icons/UserGradientCircle"
import { Optional } from "@/lib/types"
import { flaskUrl } from "@/lib/url"
import { Org, ViewType, useCurrentUserQuery } from "@/v2/graphql/public-generated-rq"
import { useAuth } from "@/v2/providers/Auth"
import {
  Button,
  Divider,
  Drawer,
  DropdownMenu,
  Spinner,
  Text,
  Truncate,
  twMerge,
} from "@lumen-energy/design-system"
import { IconCheck, IconDots, IconLogout, IconMenu2 } from "@tabler/icons-react"

import { useAppLayout } from "./AppLayoutContext"

export interface HeaderProps {
  onToggleSidebar: () => void
}

export const Header = ({ onToggleSidebar }: HeaderProps) => {
  const [hideLogo, setHideLogo] = useState(false)
  const { data } = useCurrentUserQuery()
  const logoUrl = data?.currentUser.org?.logoUrl

  return (
    <header className="sticky top-0 isolate z-30 flex h-header w-full items-center justify-between border-b border-colors-border-secondary bg-white p-5 md:hidden">
      <div className="flex items-stretch gap-3">
        <Button
          aria-label="Open sidebar"
          className="md:hidden"
          icon={IconMenu2}
          iconOnly
          noBorder
          variant="neutral"
          size="xlarge"
          onClick={onToggleSidebar}
        />
        <LumenLogo className="h-9 w-auto" />
        <div onClick={() => setHideLogo(!hideLogo)} className="flex items-center">
          {logoUrl && !hideLogo ? (
            <>
              <Divider vertical />
              <div className="h-9">
                <img src={logoUrl} alt="logo" className="max-h-full" />
              </div>
            </>
          ) : null}
        </div>
      </div>

      <div>
        <MobileHeaderDropdown />
      </div>
    </header>
  )
}

const shouldShowSwitcher = (org: Optional<Pick<Org, "externalFinancier" | "pid">>) => {
  /*
   * auxQo7WHhNmxuAbZXXyzat: The Bridge Vertical org is grandfathered into the dual view
   * jEQrs6Ka6t3Jg9fLVR5C8R: TPG sees both because they're giving PPAs to themselves
   */
  return (
    org?.externalFinancier ||
    org?.pid == "auxQo7WHhNmxuAbZXXyzat" ||
    org?.pid == "jEQrs6Ka6t3Jg9fLVR5C8R"
  )
}

export function SidebarFooterUserDropdown() {
  const { data, isLoading } = useCurrentUserQuery()
  const { isAdmin } = useAuth()
  const appLayout = useAppLayout()

  const org = data?.currentUser.org

  if (isLoading || !data) {
    return <Spinner size={28} className="hidden md:inline" />
  }

  return (
    <DropdownMenu.Root>
      <div className="flex max-w-full items-center gap-3 overflow-hidden border-t border-brand-secondary-900 pl-4 pr-3 pt-6 text-white">
        <div className="w-[40px]">
          <UserGradientCircle />
        </div>
        <div className="min-w-0 grow">
          <div className="text-body1">
            <Truncate
              text={data.currentUser.name || data.currentUser.email || "Guest"}
              disableTooltip
            />
          </div>
          {data.currentUser.name && data.currentUser.email && (
            <div className="text-body2">
              <Truncate text={data.currentUser.email} disableTooltip />
            </div>
          )}
        </div>
        <DropdownMenu.Trigger asChild>
          <div className="box-content h-6 w-6 cursor-pointer rounded-full p-1 hover:bg-brand-primary-700 aria-expanded:bg-brand-primary-700">
            <IconDots size={24} />
            <span className="sr-only">Switch view or sign out</span>
          </div>
        </DropdownMenu.Trigger>
      </div>

      <DropdownMenu.Content
        align="end"
        side="right"
        className="max-w-xs"
        showArrow
        alignOffset={-10}
        sideOffset={8}
      >
        {(isAdmin || shouldShowSwitcher(org)) && (
          <>
            {appLayout.view == ViewType.Investor && (
              <DropdownMenu.Label>You are viewing as an investor</DropdownMenu.Label>
            )}
            {appLayout.view == ViewType.Offtaker && (
              <DropdownMenu.Label>You are viewing as a PPA offtaker</DropdownMenu.Label>
            )}
            <DropdownMenu.Sub>
              <DropdownMenu.SubTrigger>Switch view</DropdownMenu.SubTrigger>
              <DropdownMenu.SubContent>
                <DropdownMenu.Item
                  onSelect={() => appLayout.setView(ViewType.Offtaker)}
                >
                  {appLayout.view === ViewType.Offtaker && <IconCheck />}
                  {appLayout.viewLabels[ViewType.Offtaker]}
                </DropdownMenu.Item>
                <DropdownMenu.Item
                  onSelect={() => appLayout.setView(ViewType.Investor)}
                >
                  {appLayout.view === ViewType.Investor && <IconCheck />}
                  {appLayout.viewLabels[ViewType.Investor]}
                </DropdownMenu.Item>
              </DropdownMenu.SubContent>
            </DropdownMenu.Sub>
            <DropdownMenu.Separator />
          </>
        )}
        <DropdownMenu.Label>
          You are signed in as {data.currentUser.email}
        </DropdownMenu.Label>
        <DropdownMenu.Item asChild>
          <a href={flaskUrl(["/logout"])}>
            <IconLogout />
            Sign out
          </a>
        </DropdownMenu.Item>
      </DropdownMenu.Content>
    </DropdownMenu.Root>
  )
}

export function MobileHeaderDropdown() {
  const { data, isLoading } = useCurrentUserQuery()
  const { isAdmin } = useAuth()
  const [showSwitcher, setShowSwitcher] = useState(false)

  const appLayout = useAppLayout()

  const org = data?.currentUser.org

  const handleSwitchAccount = useCallback(
    (view: ViewType) => () => {
      appLayout.setView(view)
      setShowSwitcher(false)
    },
    [appLayout],
  )

  if (isLoading || !data) {
    return <Spinner size={28} className="md:hidden" />
  }

  const itemClasses = twMerge(
    DropdownMenu.itemClasses,
    "pointer-events-auto hover:bg-grey-300 focus:bg-grey-300",
  )

  return (
    <Drawer.Root>
      <Drawer.Trigger asChild>
        <button aria-label="my account" className="md:hidden">
          <Avatar
            name={data.currentUser.name ?? data.currentUser.email ?? ""}
            className="bg-colors-icon-secondary text-colors-bg-primary-screen"
          />
        </button>
      </Drawer.Trigger>

      <Drawer.Content position="top" className="flex h-fit w-full flex-col pt-4">
        {(isAdmin || shouldShowSwitcher(org)) && (
          <>
            <div className="flex flex-col gap-1 p-2 pt-4">
              {appLayout.view == ViewType.Investor && (
                <Text.LabelSm className="px-2">
                  You are viewing as an investor
                </Text.LabelSm>
              )}
              {appLayout.view == ViewType.Offtaker && (
                <Text.LabelSm className="px-2">
                  You are viewing as a PPA offtaker
                </Text.LabelSm>
              )}
              <Text.LabelLg
                component="button"
                className={twMerge(itemClasses, {
                  "bg-grey-300": showSwitcher,
                })}
                onClick={() => setShowSwitcher(!showSwitcher)}
              >
                Switch account
              </Text.LabelLg>
            </div>
            {showSwitcher && (
              <>
                <Divider />
                <div className="flex flex-col gap-1 p-2 pt-4">
                  <Text.LabelLg
                    component="button"
                    className={twMerge(itemClasses, {
                      "bg-grey-300": appLayout.view === ViewType.Offtaker,
                    })}
                    onClick={handleSwitchAccount(ViewType.Offtaker)}
                  >
                    {appLayout.view === ViewType.Offtaker && <IconCheck />}
                    {appLayout.viewLabels[ViewType.Offtaker]}
                  </Text.LabelLg>
                  <Text.LabelLg
                    component="button"
                    className={twMerge(itemClasses, {
                      "bg-grey-300": appLayout.view === ViewType.Investor,
                    })}
                    onClick={handleSwitchAccount(ViewType.Investor)}
                  >
                    {appLayout.view === ViewType.Investor && <IconCheck />}
                    {appLayout.viewLabels[ViewType.Investor]}
                  </Text.LabelLg>
                </div>
              </>
            )}
          </>
        )}
        <Divider />
        <div className="flex flex-col gap-2 p-2 pt-4">
          <Text.LabelSm className="px-2 text-colors-text-secondary">
            You are signed in as {data.currentUser.email}
          </Text.LabelSm>
          <Text.LabelLg
            component="a"
            href={flaskUrl(["/logout"])}
            className={itemClasses}
          >
            Sign out
          </Text.LabelLg>
        </div>
      </Drawer.Content>
    </Drawer.Root>
  )
}

const Avatar = ({ name, className }: { name: string; className?: string }) => {
  const initials = useMemo(() => {
    const [first, last] = name.split(" ")
    return `${first?.[0] ?? ""}${last?.[0] ?? ""}`.toUpperCase()
  }, [name])

  return (
    <Text.LabelXs
      className={twMerge(
        "flex h-7 w-7 items-center justify-center rounded-full bg-brand-primary-100 group-hover/button:bg-white group-aria-expanded/button:bg-white",
        className,
      )}
    >
      {initials}
    </Text.LabelXs>
  )
}
